/**
 * The dialog frame of the overview's windows (open invoices, comments, mail). Tokens of the `.clim` root, a real
 * `role="dialog"` with a name, Escape on the app's one Escape stack, focus into the dialog on open and back to the
 * element that had it (the grid row) on close. Inline styles because the kit (A4) has no dialog class.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { useTopmostEscape } from '../../../hooks/useTopmostEscape';

interface Props {
    title: string;
    subtitle?: string;
    onClose: () => void;
    children: ReactNode;
    footer?: ReactNode;
    /** Width in px; default 880. */
    width?: number;
}

export function OverviewDialog({ title, subtitle, onClose, children, footer, width = 880 }: Props) {
    const ref = useRef<HTMLDivElement | null>(null);
    useTopmostEscape(true, onClose);

    useEffect(() => {
        const before = document.activeElement as HTMLElement | null;
        ref.current?.focus();
        return () => { if (before && before.isConnected) before.focus(); };
    }, []);

    return (
        <div
            style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'grid', placeItems: 'center', background: 'rgba(15, 23, 42, 0.4)', padding: 16 }}
            onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                ref={ref}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                tabIndex={-1}
                style={{
                    width: `min(${width}px, 100%)`, maxHeight: '86vh', display: 'flex', flexDirection: 'column',
                    background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--hair)',
                    borderRadius: 'var(--card-radius)', boxShadow: 'var(--popover-shadow)', outline: 'none',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '16px 20px', borderBottom: '1px solid var(--hair)' }}>
                    <div style={{ minWidth: 0 }}>
                        <h2 style={{ margin: 0, fontSize: 'var(--fs-h3)', lineHeight: 'var(--lh-h3)', fontWeight: 640, color: 'var(--text-strong)' }}>{title}</h2>
                        {subtitle && <p className="muted" style={{ margin: '2px 0 0' }}>{subtitle}</p>}
                    </div>
                    <button type="button" className="btn quiet sm" onClick={onClose}>Close</button>
                </div>
                <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px 20px' }}>{children}</div>
                {footer && <div style={{ padding: '12px 20px', borderTop: '1px solid var(--hair)', display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>{footer}</div>}
            </div>
        </div>
    );
}
